import _ from "lodash";
import React, { useState } from "react";
import styled from "styled-components";

import {
  CheckboxButton,
  RichContentDisplayOptions,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import Badge from "@examspark/common-ui/dist/components/text/Badge";
import { ParsedUnitObjective } from "@examspark/types";

import { useFlags } from "../../hooks/useFlags";

import SpecificityComparison from "./SpecificityComparison";
import UnitContent from "./UnitContent";

type Props = {
  objective: ParsedUnitObjective | null | undefined;
  showAnnotationNotes?: boolean;
  showAnnotationCcrs?: boolean;
  showAnnotationRcfp?: boolean;
  showEnhancedClarification?: boolean;
  header?: React.ReactNode;
};

const StyledSpecificity = styled(Text.Body)`
  .examspark-mark-tecd {
  }
`;

const StyledAnnotation = styled.div`
  padding: 1.5rem;
  border-radius: 0.25rem;
  font-size: 1.3rem;

  ${Badge} {
    color: white;
    margin-bottom: 0.5rem;
    text-align: center;
    border: none;
  }
`;

const StyledSpecificityContent = styled(StyledAnnotation)`
  border: solid 1px #ddd;

  & > div {
    color: #3b3beb;
  }

  ${Badge} {
    background-color: #666;
    margin-bottom: 2rem;
  }
`;

const StyledNotesAnnotation = styled(StyledAnnotation)`
  background-color: #f4f4f4;
  & > div {
    color: #3b3beb;
  }
  ${Badge} {
    background-color: #666;
  }
`;

const StyledCcrsRcfpAnnotation = styled(StyledAnnotation)`
  background-color: #f4f4f4;
  ${Badge} {
    background-color: #666;
  }
`;

const ObjectiveSpecificity = ({
  objective,
  showAnnotationNotes,
  showAnnotationCcrs,
  showAnnotationRcfp,
  showEnhancedClarification,
  header,
}: Props) => {
  const flags = useFlags();
  const [showFullSpecificity, setShowFullSpecificity] = useState(false);

  if (objective == null) return null;

  const customizations = objective.objectivesItem?.data;

  const annotations = _.keyBy(
    objective.annotations?.filter((a) => !!a.content?.length) ?? [],
    (a) => a.label ?? "",
  );

  const hasCustomSpecificity =
    customizations?.notes != null && customizations?.notes !== objective.notes;

  if (!(customizations?.notes ?? objective.notes)) {
    return null;
  }

  const useDiffer = flags.showObjectiveSpecificityDiffToggleButton;

  const specificityToDisplay =
    showFullSpecificity && !useDiffer
      ? objective.notes
      : (customizations?.notes ?? objective.notes);

  const renderOptions: RichContentDisplayOptions = {
    marks: {
      tecd: showEnhancedClarification ? "include" : "exclude",
    },
  };

  return (
    <VSpace size="1rem">
      {(!!header || !!hasCustomSpecificity) && (
        <Toolbar>
          {header}
          <Toolbar.Right>
            {hasCustomSpecificity && (
              <CheckboxButton
                checked={showFullSpecificity}
                onClick={() => setShowFullSpecificity(!showFullSpecificity)}
                variant="contained"
                title="Show Full Specificity"
              >
                Show Full Specificity
              </CheckboxButton>
            )}
          </Toolbar.Right>
        </Toolbar>
      )}

      {(customizations?.notes != null || objective.notes != null) && (
        <StyledSpecificity>
          {hasCustomSpecificity && showFullSpecificity && useDiffer ? (
            <SpecificityComparison
              original={objective.notes}
              customized={customizations?.notes}
              options={renderOptions}
            />
          ) : (
            <StyledSpecificityContent>
              <Badge>TEKS RS Specificity</Badge>
              <UnitContent
                content={specificityToDisplay}
                options={renderOptions}
              />
            </StyledSpecificityContent>
          )}
        </StyledSpecificity>
      )}
      {showAnnotationNotes !== false && "tcmpc.notes" in annotations && (
        <StyledNotesAnnotation>
          <Badge>TEKS RS Notes</Badge>
          <UnitContent content={annotations["tcmpc.notes"]?.content ?? ""} />
        </StyledNotesAnnotation>
      )}
      {showAnnotationRcfp !== false && "tcmpc.rcfp" in annotations && (
        <StyledCcrsRcfpAnnotation>
          <Badge>TxRCFP</Badge>
          <UnitContent content={annotations["tcmpc.rcfp"]?.content ?? ""} />
        </StyledCcrsRcfpAnnotation>
      )}
      {showAnnotationCcrs !== false && "tcmpc.ccrs" in annotations && (
        <StyledCcrsRcfpAnnotation>
          <Badge>TxCCRS</Badge>
          <UnitContent content={annotations["tcmpc.ccrs"]?.content ?? ""} />
        </StyledCcrsRcfpAnnotation>
      )}
    </VSpace>
  );
};

export default ObjectiveSpecificity;
